// Batch 7-C｜歷史健診前後比較（純 display 比較，不重新 Scoring）。
//
// 只做「已保存數值」的相減顯示，例如 newScore - oldScore、reviewCount 差值。
// 這不是重新 calculate_gmb_audit_score：不做 median、grade、coverage、priority 重算，
// 也不呼叫任何 Google / Peer / Scoring Function。

import type { RetrievedGmbAuditReport } from '@/pages/gmb-audit/data/auditApi';
import type { ScoringMetricStatus } from '@/types/gmb';

/** 單一指標的前後比較列。 */
export interface MetricComparisonRow {
  key: string;
  label: string;
  oldMaxScore: number;
  /** 僅 old status = SCORED 時有值；不足狀態為 null（不可當 0）。 */
  oldScore: number | null;
  oldStatus: ScoringMetricStatus | null;
  newMaxScore: number;
  newScore: number | null;
  newStatus: ScoringMetricStatus | null;
  /** 兩邊皆 SCORED 且 scoringModelVersion 相同才可比較。 */
  comparable: boolean;
  /** comparable 為 true 時為 newScore - oldScore，否則 null。 */
  delta: number | null;
}

/** 指標狀態 → 一般人可理解的文字（不把不足狀態當 0）。 */
export function metricStatusLabel(status: ScoringMetricStatus | null | undefined): string {
  switch (status) {
    case 'SCORED':
      return '已計分';
    case 'INSUFFICIENT_DATA':
      return '資料不足';
    case 'INSUFFICIENT_PEER_DATA':
      return '同業資料不足';
    case 'NOT_APPLICABLE':
      return '不適用';
    default:
      return '未提供';
  }
}

/** 指標單邊顯示文字：SCORED 顯示「8 / 12」，其餘顯示狀態文字。 */
export function formatMetricSide(
  score: number | null,
  maxScore: number,
  status: ScoringMetricStatus | null | undefined,
): string {
  if (status === 'SCORED' && typeof score === 'number') {
    return `${score} / ${maxScore}`;
  }
  return metricStatusLabel(status);
}

function readScoredScore(
  metric: { status: ScoringMetricStatus; score?: number } | undefined,
): number | null {
  if (metric && metric.status === 'SCORED' && typeof metric.score === 'number') {
    return metric.score;
  }
  return null;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * 依 metric.key 對位（不使用 array index 假定順序永遠一致），
 * 產生 11 項指標的前後比較列。兩邊皆 SCORED 且 scoringModelVersion 相同才顯示差值。
 */
export function buildMetricComparisons(
  oldReport: RetrievedGmbAuditReport,
  newReport: RetrievedGmbAuditReport,
  sameScoringModelVersion: boolean,
): MetricComparisonRow[] {
  const oldMetrics = oldReport.report.scoreResult.metrics;
  const newMetrics = newReport.report.scoreResult.metrics;

  const oldMap = new Map(oldMetrics.map((m) => [m.key, m]));
  const newMap = new Map(newMetrics.map((m) => [m.key, m]));

  // 以新版順序為主，補上僅存在於舊版的 key（不因未來順序變動而漏項）。
  const orderedKeys: string[] = [];
  const seen = new Set<string>();
  newMetrics.forEach((m) => {
    if (!seen.has(m.key)) {
      seen.add(m.key);
      orderedKeys.push(m.key);
    }
  });
  oldMetrics.forEach((m) => {
    if (!seen.has(m.key)) {
      seen.add(m.key);
      orderedKeys.push(m.key);
    }
  });

  return orderedKeys.map((key) => {
    const om = oldMap.get(key);
    const nm = newMap.get(key);
    const oldScore = readScoredScore(om);
    const newScore = readScoredScore(nm);
    const comparable = sameScoringModelVersion && oldScore !== null && newScore !== null;
    return {
      key,
      label: nm?.label ?? om?.label ?? key,
      oldMaxScore: om?.maxScore ?? nm?.maxScore ?? 0,
      oldScore,
      oldStatus: om?.status ?? null,
      newMaxScore: nm?.maxScore ?? om?.maxScore ?? 0,
      newScore,
      newStatus: nm?.status ?? null,
      comparable,
      delta: comparable ? round2((newScore as number) - (oldScore as number)) : null,
    };
  });
}

/** 數值差值（兩邊皆為有限數字才回傳），保留兩位小數。 */
export function numericDelta(oldValue: number | null | undefined, newValue: number | null | undefined): number | null {
  if (typeof oldValue !== 'number' || typeof newValue !== 'number') return null;
  if (!Number.isFinite(oldValue) || !Number.isFinite(newValue)) return null;
  return round2(newValue - oldValue);
}

/** 分數差值顯示：>0 → +XX，=0 → 無變化，<0 → -XX。 */
export function formatScoreDelta(delta: number | null): string {
  if (delta === null) return '無法直接比較';
  if (delta === 0) return '無變化';
  return delta > 0 ? `+${delta} 分` : `${delta} 分`;
}

/** 一般數值差值顯示（帶正負號與單位）。 */
export function formatDelta(delta: number | null, unit = ''): string {
  if (delta === null) return '無法直接比較';
  if (delta === 0) return '無變化';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta}${unit}`;
}